import { IClinicListResponseData } from "@/api_framework/api_modals/Clinics";
import { IOrgCityListResponseData, IOrgProviderListResponseData } from "@/api_framework/api_modals/Org";
import {
  ERRORS,
  IRotaErrorListData,
  IRotaManagementRotaCategoryData,
  IRotaManagementRotaDetailsData,
  IRotaManagementRotaFilterData,
  IRotaManagementRotaFilterParams,
  IRotaManagementRotaProvidersData,
  IRotaManagementShiftDetailsData,
  IRotaManagementShiftListData,
  IRotaManagementShiftStatsData,
  IRotaOverbookingEligibleService,
  IRotaOverbookingRequest,
} from "@/api_framework/api_modals/Rota";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { IRouteReducerSliceStateInit } from "../slice_interface";

export type RotaStateKeys =
  | "category"
  | "provider"
  | "shiftList"
  | "filterData"
  | "rotaDetails"
  | "rotaShiftName"
  | "copyShiftData"
  | "rotaErrorList"
  | "clinics"
  | "cities"
  | "providers"
  | "RotaFilter"
  | "selectedStafForCopy"
  | "rotaerrorIds"
  | "overbookingEligibleServices"
  | "overbookingRequests"
  | "overbookingRequestsTotalCount"
  | "overbookingRequestFilters";

export interface IOverbookingRequestFiltersState {
  clinicId: string;
  clinicName: string;
  type: string;
  date: string;
}

export interface IRotaState {
  category?: IRotaManagementRotaCategoryData[] | null;
  provider?: IRotaManagementRotaProvidersData[] | null;
  shiftStats?: IRotaManagementShiftStatsData | null;
  shiftList?: IRotaManagementShiftListData[] | null;
  rotaShiftName?: IRotaManagementShiftListData[] | null;
  shiftDetails?: IRotaManagementShiftDetailsData | null;
  filterData?: IRotaManagementRotaFilterData[] | null;
  rotaDetails?: IRotaManagementRotaDetailsData[] | null;
  rotaErrorList: IRotaErrorListData | null;
  clinics?: IClinicListResponseData[] | null;
  cities?: IOrgCityListResponseData[] | null;
  providers?: IOrgProviderListResponseData[] | null;
  RotaFilter?: IRotaManagementRotaFilterParams | null;
  selectedStafForCopy?: Array<string> | null;
  rotaerrorIds?: ERRORS | null;
  overbookingEligibleServices?: IRotaOverbookingEligibleService[] | null;
  overbookingRequests?: IRotaOverbookingRequest[] | null;
  overbookingRequestsTotalCount?: number;
  overbookingRequestFilters?: IOverbookingRequestFiltersState | null;
}
export interface IRotaInitialState extends IRouteReducerSliceStateInit {
  state: IRotaState;
}

const initialState: IRotaInitialState = {
  state: {
    category: [],
    provider: [],
    shiftStats: null,
    shiftList: null,
    shiftDetails: null,
    filterData: [],
    rotaDetails: [],
    rotaShiftName: null,
    rotaErrorList: null,
    RotaFilter: null,
    selectedStafForCopy: null,
    rotaerrorIds: null,
    overbookingEligibleServices: null,
    overbookingRequests: null,
    overbookingRequestsTotalCount: 0,
    overbookingRequestFilters: null,
  },
};

const rotaSlice = createSlice({
  name: "rota_slice",
  initialState: initialState,
  reducers: {
    setROTAOrgCities: (
      state,
      action: PayloadAction<{
        data: IOrgCityListResponseData[];
      }>,
    ) => {
      state.state.cities = action.payload.data;
    },
    setROTAOrgProviders: (
      state,
      action: PayloadAction<{
        data: IOrgProviderListResponseData[];
      }>,
    ) => {
      state.state.providers = action.payload.data;
    },
    setROTAOrgClinics: (
      state,
      action: PayloadAction<{
        data: IClinicListResponseData[];
      }>,
    ) => {
      state.state.clinics = action.payload.data;
    },
    setCategory: (state, action: PayloadAction<{ data: IRotaManagementRotaCategoryData[] }>) => {
      state.state.category = action.payload.data;
    },
    setProvider: (state, action: PayloadAction<{ data: IRotaManagementRotaProvidersData[] }>) => {
      state.state.provider = action.payload.data;
    },
    setShiftStats: (state, action: PayloadAction<{ data: IRotaManagementShiftStatsData }>) => {
      state.state.shiftStats = action.payload.data;
    },
    setShiftList: (state, action: PayloadAction<{ data: IRotaManagementShiftListData[] }>) => {
      state.state.shiftList = action.payload.data;
    },
    setShiftDetails: (state, action: PayloadAction<{ data: IRotaManagementShiftDetailsData | null }>) => {
      state.state.shiftDetails = action.payload.data;
    },
    setRotaFilterData: (state, action: PayloadAction<{ data: IRotaManagementRotaFilterData[] }>) => {
      state.state.filterData = action.payload.data;
    },
    setRotaDetails: (state, action: PayloadAction<{ data: IRotaManagementRotaDetailsData[] }>) => {
      state.state.rotaDetails = action.payload.data;
    },

    setRotaShiftName: (state, action: PayloadAction<{ data: IRotaManagementShiftListData[] }>) => {
      state.state.rotaShiftName = action.payload.data;
    },
    setRotaErrorList: (state, action: PayloadAction<{ data: IRotaErrorListData }>) => {
      state.state.rotaErrorList = action.payload.data;
    },
    setRotaFilter: (state, action: PayloadAction<{ data: IRotaManagementRotaFilterParams }>) => {
      state.state.RotaFilter = action.payload.data;
    },

    setSelectedStafForCopy: (state, action: PayloadAction<Array<string>>) => {
      state.state.selectedStafForCopy = action.payload;
    },
    setRotaerrorIds: (state, action: PayloadAction<{ data: ERRORS }>) => {
      state.state.rotaerrorIds = action.payload.data;
    },
    setOverbookingEligibleServices: (state, action: PayloadAction<{ data: IRotaOverbookingEligibleService[] }>) => {
      state.state.overbookingEligibleServices = action.payload.data;
    },
    setOverbookingRequests: (
      state,
      action: PayloadAction<{ data: IRotaOverbookingRequest[] | null; totalCount?: number }>,
    ) => {
      state.state.overbookingRequests = action.payload.data;
      state.state.overbookingRequestsTotalCount = action.payload.totalCount ?? 0;
    },
    setOverbookingRequestFilters: (
      state,
      action: PayloadAction<{
        data: Partial<IOverbookingRequestFiltersState> | null;
      }>,
    ) => {
      if (!action.payload.data) {
        state.state.overbookingRequestFilters = null;
        return;
      }

      const previousFilters = state.state.overbookingRequestFilters ?? {
        clinicId: "",
        clinicName: "",
        type: "",
        date: "",
      };

      state.state.overbookingRequestFilters = {
        ...previousFilters,
        ...action.payload.data,
      };
    },
  },
});

export const {
  setCategory,
  setProvider,
  setShiftStats,
  setShiftList,
  setShiftDetails,
  setRotaFilterData,
  setRotaDetails,
  setRotaShiftName,
  setRotaErrorList,
  setROTAOrgCities,
  setROTAOrgClinics,
  setROTAOrgProviders,
  setRotaFilter,
  setSelectedStafForCopy,
  setRotaerrorIds,
  setOverbookingEligibleServices,
  setOverbookingRequests,
  setOverbookingRequestFilters,
} = rotaSlice.actions;
export default rotaSlice.reducer;
